import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import {
  ContactEmailAddress,
  ContactEmailAddressService,
} from '@app/contacts/shared/contact-email-address.service';

/**
 * TVG-342: the contact's extra email addresses.
 *
 * A reply from an address no contact holds lands on the Communications page as
 * Not filed. Staff file it by adding that address here: the waiting email moves
 * onto this contact, and every later email from the address files itself.
 */
@Component({
  selector: 'app-contact-email-addresses',
  standalone: true,
  imports: [ButtonComponent, TextInputComponent],
  templateUrl: './contact-email-addresses.component.html',
})
export class ContactEmailAddressesComponent implements OnInit, OnChanges {
  @Input({ required: true }) contactId!: number;
  /** Adding and removing follow the rule for editing the contact. */
  @Input() canEdit = false;

  /** Emitted with the number of Not filed emails an added address filed here. */
  @Output() filed = new EventEmitter<number>();

  protected addresses: ContactEmailAddress[] = [];
  protected loading = false;
  protected saving = false;
  protected error: string | null = null;
  protected notice: string | null = null;

  protected readonly emailControl = new FormControl('', [Validators.required, Validators.email]);

  constructor(private addressService: ContactEmailAddressService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId'] && !changes['contactId'].firstChange) {
      this.load();
    }
  }

  protected add(): void {
    const email = (this.emailControl.value ?? '').trim();

    if (this.emailControl.invalid || email === '' || this.saving) {
      this.emailControl.markAsTouched();
      return;
    }

    this.saving = true;
    this.error = null;
    this.notice = null;

    this.addressService.add(this.contactId, email).subscribe({
      next: (response) => {
        this.saving = false;
        const filed = response.filed === 1 ? 'One email' : response.filed + ' emails';
        if (response.data) {
          const added = response.data;
          this.addresses = [...this.addresses, added].sort((a, b) => a.email.localeCompare(b.email));
          this.notice =
            response.filed > 0
              ? `${added.email} added. ${filed} from it filed on this contact.`
              : `${added.email} added.`;
        } else {
          // The contact already held it: only the emails waiting from it were filed.
          this.notice = `${filed} from ${email} filed on this contact.`;
        }
        this.emailControl.reset('');

        if (response.filed > 0) {
          this.filed.emit(response.filed);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.error = err.error?.message ?? 'The address could not be added.';
      },
    });
  }

  protected remove(address: ContactEmailAddress): void {
    this.error = null;
    this.notice = null;

    this.addressService.remove(this.contactId, address.id).subscribe({
      next: () => {
        this.addresses = this.addresses.filter((existing) => existing.id !== address.id);
      },
      error: () => {
        this.error = 'The address could not be removed.';
      },
    });
  }

  private load(): void {
    this.loading = true;

    this.addressService.list(this.contactId).subscribe({
      next: (response) => {
        this.loading = false;
        this.addresses = response.data;
      },
      error: () => {
        this.loading = false;
        this.error = 'The extra email addresses could not be loaded.';
      },
    });
  }
}
